import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import React, { useMemo } from 'react';
import { useChatHistory } from '../hooks/useChatHistory';
import { useStreamingConnection } from '../hooks/useStreamingConnection';
require('@chatscope/chat-ui-kit-styles/dist/default/styles.min.css');
require('./styles/style-overrides.css');

import {
  Avatar,
  ChatContainer,
  InputToolbox,
  MainContainer,
  Message,
  MessageInput,
  MessageList,
  type MessageInputProps,
} from '@chatscope/chat-ui-kit-react';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import {
  AttachmentStatus,
  NewAttachment,
  PostDetails_ActivePost_Fragment,
  PostDetailsFragment,
  PostOrder,
  useCreatePostReactionMutation,
  useCreatePrivatePostMutation,
} from '../__generated__/api.graphql';
import {
  CLINICIAN_CHAT_DEFAULT_MODEL_ID,
  CLINICIAN_CHAT_MODEL_STORAGE_KEY,
  CLINICIANS_AGENT_USERNAME,
  isClaude5OrNewer,
  MAX_MESSAGE_CHARS,
  MEMBERS_AGENT_USERNAME,
  MESSAGE_CHAR_COUNTER_THRESHOLD,
  POPULATION_AGENT_USERNAME,
  POPULATION_CHAT_MODEL_STORAGE_KEY,
} from '../constants';
import AgentIcon from '../images/ai-bot.svg';

import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import {
  ArrowDown2,
  MessageCircle,
  Paperclip,
  Settings,
  ThumbsDown,
  ThumbsUp,
  X,
} from '@lifeomic/chromicons';
import EmptyPageState from '@lifeomic/phc-web-toolkit/src/components/EmptyPageState';
import {
  useActiveAccountId,
  useActiveProjectCohortId,
  useActiveProjectId,
  useFeatures,
  useNotify,
  useSessionStorageState,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useMixpanel } from '@lifeomic/phc-web-toolkit/src/hooks/useMixpanel';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { apiHooks } from '@lifeomic/phc-web-toolkit/src/hooks/useRestAPIHooks';
import FhirService from '@lifeomic/phc-web-toolkit/src/services/FhirService';
import clsx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { v4 } from 'uuid';
import { text } from '../text';
import { ChatMessage } from './ChatMessage';
import { Citation } from './Citation';
import {
  ChatAttachmentMessage,
  ChatAttachmentNode,
} from './ChatAttachmentMessage';
import {
  AttachmentViewerContext,
  AttachmentViewerModal,
} from '@lifeomic/phc-web-toolkit/src/components/Messaging/AttachmentViewer';
import { ModelSelect } from '@lifeomic/phc-web-toolkit/src/components/AITemplates/GenerationMenu/ModelSelect';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Popover } from '@lifeomic/chroma-react/components/Popover';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { buildZoriChatReportClinicalImpression } from '../utils/zoriReportExport';
import {
  ATTACHMENT_FILE_PICKER_ACCEPT,
  MAX_CHAT_ATTACHMENTS,
  MAX_CHAT_ATTACHMENT_BYTES,
  validateAttachmentFile,
  type AttachmentRejectionReason,
} from '../utils/chatAttachments';
import {
  useChatAttachmentUpload,
  type StagedAttachment,
} from '../hooks/useChatAttachmentUpload';
import { reportErrorToBugsnag } from '../../../../src/util/errorReporting/directReporting';

/** Upstream `MessageInputProps` omits `plaintextOnly`; the component still accepts it at runtime. */
const MessageInputWithPlaintext = MessageInput as React.ComponentType<
  MessageInputProps & { plaintextOnly?: boolean }
>;

const CONVERSATION_DISPLAY_TITLE_METADATA_KEY = 'conversationDisplayTitle';

/**
 * The forum 👎 reaction has no field for the "ask Zori to fix" choice, so we
 * ride it along in the reaction comment via these sentinel suffixes. The agents
 * `forum-change-stream` handler parses and strips them before the comment is
 * shown (see `fountain_agents/lib/reaction_handlers.py`). Keep in sync.
 */
const FIX_REQUESTED_MARKER = '[[zori:fix-requested]]';
const FIX_NOT_REQUESTED_MARKER = '[[zori:fix-not-requested]]';

const fhirService = new FhirService('Person');

const createEmptyAttachmentsV2 = () => ({
  __typename: 'AttachmentConnection' as const,
  pageInfo: {
    __typename: 'PageInfo' as const,
    endCursor: null,
    hasNextPage: false,
  },
  edges: [],
});

/**
 * Optimistic stand-ins for files that were just uploaded, so the sent message
 * shows them immediately instead of appearing to have lost them until the
 * post is refetched.
 *
 * They carry no URL and are marked `PROCESSING`, which renders a labelled but
 * un-clickable row — accurate, since the file really is still being scanned and
 * is not downloadable yet.
 */
const createEagerAttachmentsV2 = (
  uploaded: NewAttachment[],
  staged: StagedAttachment[],
): PostDetails_ActivePost_Fragment['attachmentsV2'] => {
  if (uploaded.length === 0) {
    return createEmptyAttachmentsV2();
  }

  return {
    ...createEmptyAttachmentsV2(),
    edges: uploaded.map(attachment => ({
      __typename: 'AttachmentEdge' as const,
      node: {
        __typename: 'Attachment' as const,
        id: attachment.externalId,
        externalId: attachment.externalId,
        url: null,
        originalUrl: null,
        type: attachment.type,
        subType: attachment.subType,
        thumbnails: [],
        status: AttachmentStatus.PROCESSING,
        fileName:
          attachment.fileName ??
          staged.find(candidate => candidate.id === attachment.id)?.file.name ??
          null,
      },
    })),
  };
};

const messages = defineMessages({
  deletedMessage: {
    id: 'agentChat.chat.deletedMessage',
    defaultMessage: '<DELETED>',
  },
  feedbackNegative: {
    id: 'agentChat.chat.feedbackNegative',
    defaultMessage: 'Needs Improvement',
  },
  reportAndAskZoriToFix: {
    id: 'agentChat.chat.reportAndAskZoriToFix',
    defaultMessage: 'Report and Ask Zori to Fix',
  },
  reportIssueOnly: {
    id: 'agentChat.chat.reportIssueOnly',
    defaultMessage: 'Report issue only',
  },
  feedbackPositive: {
    id: 'agentChat.chat.feedbackPositive',
    defaultMessage: 'Good Response',
  },
  leaveFeedbackTitle: {
    id: 'agentChat.chat.leaveFeedbackTitle',
    defaultMessage: 'Your feedback helps us improve our AI Chat experience',
  },
  placeholder: {
    id: 'agentChat.chat.placeholder',
    defaultMessage: 'Ask a question…',
  },
  placeholderFeedback: {
    id: 'agentChat.chat.placeholderFeedback',
    defaultMessage: 'Let us know how we can improve…',
  },
  scrollToBottom: {
    id: 'agentChat.chat.scrollToBottom',
    defaultMessage: 'Latest Messages',
  },
  citationTitle: {
    id: 'agentChat.chat.citationTitle',
    defaultMessage: 'Citation Details',
  },
  errorLoadingMore: {
    id: 'agentChat.chat.errorLoadingMore',
    defaultMessage:
      'Error loading more messages. If this continues, please notify support.',
  },
  zoriChatSettings: {
    id: 'agentChat.chat.zoriChatSettings',
    defaultMessage: 'Zori chat settings',
  },
  exportToZoriReportSuccess: {
    id: 'agentChat.chat.exportToZoriReportSuccess',
    defaultMessage: 'Message exported to Zori Reports.',
  },
  exportToZoriReportFailure: {
    id: 'agentChat.chat.exportToZoriReportFailure',
    defaultMessage: 'Failed to export message to Zori Reports.',
  },
  feedbackSubmitted: {
    id: 'agentChat.chat.feedbackSubmitted',
    defaultMessage: 'Thanks for your feedback.',
  },
  feedbackError: {
    id: 'agentChat.chat.feedbackError',
    defaultMessage: 'Failed to submit feedback. Please try again.',
  },
  characterCount: {
    id: 'agentChat.chat.characterCount',
    defaultMessage: '{count} / {max} characters',
  },
  messageTooLong: {
    id: 'agentChat.chat.messageTooLong',
    defaultMessage:
      'Message is too long ({count} / {max} characters). Shorten it, or attach it as a file.',
  },
  attachFile: {
    id: 'agentChat.chat.attachFile',
    defaultMessage: 'Attach a file',
  },
  removeAttachment: {
    id: 'agentChat.chat.removeAttachment',
    defaultMessage: 'Remove {fileName}',
  },
  attachmentsNotSavedToChart: {
    id: 'agentChat.chat.attachmentsNotSavedToChart',
    defaultMessage:
      'Shared with Zori for this conversation only — not saved to the chart.',
  },
  attachmentUnsupportedType: {
    id: 'agentChat.chat.attachmentUnsupportedType',
    defaultMessage:
      '{fileName} can’t be attached. Supported types are PDF, PNG, JPG, TXT, CSV, MD, and JSON.',
  },
  attachmentTooLarge: {
    id: 'agentChat.chat.attachmentTooLarge',
    defaultMessage: '{fileName} is larger than {maxMb} MB.',
  },
  attachmentEmpty: {
    id: 'agentChat.chat.attachmentEmpty',
    defaultMessage: '{fileName} is empty.',
  },
  attachmentLimitReached: {
    id: 'agentChat.chat.attachmentLimitReached',
    defaultMessage: 'You can attach up to {max} files to a message.',
  },
  attachmentUploadFailed: {
    id: 'agentChat.chat.attachmentUploadFailed',
    defaultMessage:
      'Your attachments could not be uploaded, so the message was not sent. Please try again.',
  },
  attachmentsUploading: {
    id: 'agentChat.chat.attachmentsUploading',
    defaultMessage: 'Uploading attachments…',
  },
});

const getPaddingTop = (
  theme: Theme,
  loadingMore: boolean,
  scrollToBottomVisible: boolean,
) => {
  if (loadingMore && scrollToBottomVisible) return theme.spacing(7.5);
  if (loadingMore) return theme.spacing(2.5);
  if (scrollToBottomVisible) return theme.spacing(5);
  return 0;
};

const useStyles = makeStyles((theme: Theme) => ({
  mainContainer: ({
    loadingMore,
    scrollToBottomVisible,
  }: {
    loadingMore: boolean;
    scrollToBottomVisible: boolean;
  }) => ({
    paddingTop: getPaddingTop(theme, loadingMore, scrollToBottomVisible),
  }),
  chatRoot: ({
    loadingMore,
    scrollToBottomVisible,
  }: {
    loadingMore: boolean;
    scrollToBottomVisible: boolean;
  }) => ({
    overflow: 'hidden',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height:
      loadingMore || scrollToBottomVisible
        ? `calc(100% - ${getPaddingTop(
            theme,
            loadingMore,
            scrollToBottomVisible,
          )}px)`
        : '100%',
  }),
  startingContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    justifyItems: 'space-between',
    padding: '1rem',
    color: theme.palette.text.secondary,
  },
  typing: {
    backgroundColor: 'transparent',
  },
  startingMessage: {
    width: '100%',
    color: theme.palette.text.secondary,
    fontStyle: 'normal',
  },
  fullWidth: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullHeight: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  feedbackTextArea: {
    width: '100%',
    padding: theme.spacing(1),
    borderColor: theme.palette.divider,
  },
  scrollToBottomButton: {
    border: `solid ${theme.pxToRem(1)} ${theme.palette.primary.main}`,
    borderRadius: theme.spacing(2),
    boxShadow: theme.boxShadows.table,
    paddingLeft: theme.spacing(2),
    '&:hover, &:focus': {
      opacity: 1,
    },
  },
  scrollToBottomContainer: {
    zIndex: 2,
    position: 'absolute',
    top: theme.spacing(-6), // negative to hide it
    transition: 'top 0.3s ease',
  },
  scrollToBottomContainerVisible: {
    top: theme.spacing(2),
  },
  attachmentViewerImage: {
    maxWidth: '100%',
    maxHeight: '100vh',
    objectFit: 'contain',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  // ChatContainer fixes its slot order, so InputToolbox always renders *below*
  // the message input. The action cluster is therefore lifted back up over the
  // input to sit inside its bottom-right corner: `bottom: 100%` puts it on the
  // toolbox's top edge (flush with the input's bottom) and the margin clears
  // the input's own 1rem padding plus enough to centre against the editor.
  // Anchoring to the toolbox's *top* rather than a distant ancestor's bottom is
  // what keeps the icons put as the tray below grows with chips and counters.
  composerActions: {
    position: 'absolute',
    // Matches where the settings gear sat before the attach button existed.
    right: theme.spacing(1),
    bottom: '100%',
    // Clears the message input's own 1rem padding so the cluster's bottom edge
    // lines up with the editor's. An IconButton is the same height as the
    // single-line editor, so this reads as vertically centred.
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    zIndex: 1,
  },
  // Widen the editor's right gutter so typed text clears both icons. The base
  // override in style-overrides.css only reserves room for the gear, so this is
  // applied when the attach button is present too.
  composerWithAttachments: {
    '& .cs-message-input__content-editor-wrapper': {
      paddingRight: theme.pxToRem(80),
    },
  },
  zoriCharacterCounter: {
    alignSelf: 'center',
  },
  attachmentTray: {
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 2),
    maxWidth: '100%',
    minWidth: 0,
  },
  attachmentTrayControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  attachmentChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  attachmentChip: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    maxWidth: theme.pxToRem(220),
    padding: theme.spacing(0.25, 0.5, 0.25, 1),
    borderRadius: theme.spacing(2),
    border: `solid ${theme.pxToRem(1)} ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  attachmentChipName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  visuallyHiddenInput: {
    display: 'none',
  },
  zoriSettingsFlyout: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    minWidth: theme.pxToRem(280),
    ...(theme.palette.mode === 'dark' && {
      color: theme.palette.text.primary,
      '& p': {
        color: theme.palette.text.secondary,
      },
    }),
  },
  zoriSettingsPopover: {
    width: 300,
    zIndex: 1400, // appear higher than footer chat flyover
  },
  chatColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minWidth: 0,
  },
  chatContainer: {
    flex: 1,
    minHeight: 0,
  },
}));

// How long the deep-linked message stays visually flagged after the chat
// scrolls it into view.
const CITATION_HIGHLIGHT_DURATION_MS = 2400;
// Bound how many older pages we'll auto-load while hunting for a deep-linked
// message, and how many times we re-check the DOM between loads/renders.
const CITATION_MAX_PAGES_TO_LOAD = 10;
const CITATION_MAX_DOM_ATTEMPTS = 20;
const CITATION_RETRY_INTERVAL_MS = 50;

export type MemberThread = {
  /** The member whose posts the thread was loaded from. */
  subjectId: string;
  /** Forum thread (parent) id, which is also the agent's session id. */
  threadId: string;
};

export const Chat = ({
  patient,
  isMemberChatHistory,
  agentUsername = CLINICIANS_AGENT_USERNAME,
  userIdsOverride,
  onConversationDisplayTitle,
  onMemberThread,
  highlightMessageId,
  highlightNonce,
}: {
  patient?: fhir.Patient;
  isMemberChatHistory?: boolean;
  agentUsername?: string;
  userIdsOverride?: string[];
  onConversationDisplayTitle?: (title: string) => void;
  /**
   * Reports the member Zori thread currently loaded, or `undefined` while
   * there is none. Only the posts know the id: unlike the clinician chat,
   * whose thread id this component composes from the participant list, the
   * member thread is looked up by participant and its id is whatever
   * forum-service already stored. The member it belongs to is reported with
   * it so a caller cannot pair it with whoever else happens to be in view.
   */
  onMemberThread?: (thread: MemberThread | undefined) => void;
  /** Forum post id of a chat message to scroll to and briefly highlight. */
  highlightMessageId?: string;
  /**
   * Bumps each time the same `highlightMessageId` is re-requested so repeated
   * clicks on the same citation re-trigger the scroll/highlight.
   */
  highlightNonce?: number;
}) => {
  const { formatMessage } = useIntl();
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [componentLoaded, setComponentLoaded] = React.useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
  const [isSendingFeedback, setIsSendingFeedback] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const [currentReference, setCurrentReference] = React.useState<string>('');
  const [hoveredPostId, setHoveredPostId] = React.useState<string | null>(null);
  const [draftLength, setDraftLength] = React.useState(0);
  const [stagedAttachments, setStagedAttachments] = React.useState<
    StagedAttachment[]
  >([]);
  const [isUploadingAttachments, setIsUploadingAttachments] =
    React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const getListRef = () => document.querySelector('[data-cs-message-list]');
  const heightRef = React.useRef<number | null>(null);
  const accountId = useActiveAccountId();
  const projectId = useActiveProjectId();
  const cohortId = useActiveProjectCohortId();
  const attachmentViewer = React.useContext(AttachmentViewerContext);
  const { activeAttachmentImage, setActiveAttachmentImage } =
    attachmentViewer ?? {};

  const isClinicianZoriChat =
    !isMemberChatHistory && agentUsername === CLINICIANS_AGENT_USERNAME;
  const isPopulationAgentChat =
    !isMemberChatHistory && agentUsername === POPULATION_AGENT_USERNAME;
  const showZoriInputToolbox = isClinicianZoriChat || isPopulationAgentChat;
  const hasFeature = useFeatures();
  const [selectedModelId, setSelectedModelId] = useSessionStorageState<
    string | undefined
  >(
    isClinicianZoriChat
      ? CLINICIAN_CHAT_MODEL_STORAGE_KEY
      : POPULATION_CHAT_MODEL_STORAGE_KEY,
    undefined,
  );
  // The clinician surface adds the latest models to the list.
  const modelsQuery = apiHooks.useAPIQuery(
    'GET /v1/agents-v2/models',
    isClinicianZoriChat ? { surface: 'clinician' } : {},
  );
  const allModels = modelsQuery.data?.models ?? [];
  const models = isClinicianZoriChat
    ? allModels.filter(model => isClaude5OrNewer(model.model_id))
    : allModels;
  // A saved choice can name a model this list no longer offers.
  const modelId = models.some(model => model.model_id === selectedModelId)
    ? selectedModelId
    : isClinicianZoriChat
    ? CLINICIAN_CHAT_DEFAULT_MODEL_ID
    : undefined;
  const feedbackAgentUsername = isMemberChatHistory
    ? MEMBERS_AGENT_USERNAME
    : agentUsername;

  const isDraftOverLimit = draftLength > MAX_MESSAGE_CHARS;
  const showCharacterCounter = draftLength >= MESSAGE_CHAR_COUNTER_THRESHOLD;

  const attachmentsEnabled =
    showZoriInputToolbox && hasFeature('clinicianChatAttachments');

  const { track } = useMixpanel();
  // The adoption contract scopes the Zori usefulness funnel to the clinician
  // and population chats; member chat history stays out of it.
  const zoriSurface = isClinicianZoriChat
    ? ('clinician' as const)
    : isPopulationAgentChat
    ? ('population' as const)
    : undefined;

  const handleTerminalResponse = React.useCallback(
    ({ outcome }: { outcome: 'completed' | 'error' }) => {
      if (!zoriSurface) {
        return;
      }
      track('zori_chat_response_received', { surface: zoriSurface, outcome });
    },
    [track, zoriSurface],
  );

  const [getUser, getUserState] = useService(fhirService.get);
  const [resolvedUsername, setResolvedUsername] = React.useState<{
    patientId?: string;
    username: string;
  }>({ username: '' });
  const hasMadeFetch = !!getUserState.lastFetchAttempt;

  React.useEffect(() => {
    const fetchUser = async () => {
      const params = {
        identifier: 'http://lifeomic.com/fhir/username|',
        patient: patient?.id,
        _tag: `http://lifeomic.com/fhir/dataset|${projectId}`,
        cohort: cohortId,
      };

      return await getUser(accountId, params);
    };

    fetchUser().then(user => {
      setResolvedUsername({
        patientId: patient?.id,
        username: user?.data?.entry[0]?.resource?.identifier?.[0]?.value || '',
      });
    });
  }, [getUser, patient?.id, accountId, hasMadeFetch, cohortId, projectId]);

  // The lookup is asynchronous, so switching members leaves the previous
  // member's username resolved for a round trip. Keying the history query off
  // it would show one member's conversation while another is in view, so it
  // counts as unresolved until the lookup for this member comes back.
  const patientUsername =
    resolvedUsername.patientId === patient?.id ? resolvedUsername.username : '';

  const {
    userId,
    loading,
    posts,
    hasNextPage,
    fetchMore,
    loadMore,
    startPolling,
    addEagerPost,
  } = useChatHistory({
    patientId: patient?.id,
    isMemberChatHistory,
    patientUsername,
    agentUsername: feedbackAgentUsername,
    userIdsOverride,
  });

  const userIds = (
    userIdsOverride || [
      userId,
      feedbackAgentUsername,
      ...(patient?.id ? [`scope?subject=${patient?.id}`] : []),
    ]
  ).filter(Boolean) as string[];

  const parentId = [...userIds].sort().map(encodeURIComponent).join('#');

  // TODO: subscribe to chunks and render them
  const { messages: streamingMessages } = useStreamingConnection(
    `conversation:${parentId}`,
    { onTerminal: handleTerminalResponse },
  );
  const lastConversationDisplayTitleRef = React.useRef<string>();

  // Clear per-conversation dedupe so the notifier can re-emit the same title
  // string after the user switches conversations.
  React.useEffect(() => {
    lastConversationDisplayTitleRef.current = undefined;
  }, [parentId]);

  const conversationDisplayTitle = useMemo(() => {
    const getTitleFromMetadata = (
      metadata?: string | Record<string, unknown> | null,
    ) => {
      let parsedMetadata: Record<string, unknown> | null | undefined;
      try {
        parsedMetadata =
          typeof metadata === 'string'
            ? JSON.parse(metadata || '{}')
            : metadata;
      } catch {
        parsedMetadata = undefined;
      }
      const title = parsedMetadata?.[CONVERSATION_DISPLAY_TITLE_METADATA_KEY];
      return typeof title === 'string' ? title.trim() : undefined;
    };

    for (let index = streamingMessages.length - 1; index >= 0; index -= 1) {
      const message = streamingMessages[index];
      const title = getTitleFromMetadata(message.metadata);
      if (title) return title;
    }

    for (const post of posts) {
      if (post.__typename !== 'ActivePost') continue;
      const title = getTitleFromMetadata(post.metadata);
      if (title) return title;
    }

    return undefined;
  }, [posts, streamingMessages]);

  React.useEffect(() => {
    if (
      !isPopulationAgentChat ||
      !conversationDisplayTitle ||
      conversationDisplayTitle === lastConversationDisplayTitleRef.current
    ) {
      return;
    }
    lastConversationDisplayTitleRef.current = conversationDisplayTitle;
    onConversationDisplayTitle?.(conversationDisplayTitle);
  }, [
    conversationDisplayTitle,
    isPopulationAgentChat,
    onConversationDisplayTitle,
    parentId,
  ]);

  // An empty `patientUsername` means the loaded posts are not this member's,
  // so there is no thread to report rather than a stale one.
  const memberThreadId =
    isMemberChatHistory && patientUsername
      ? posts.find(post => post.parentId)?.parentId
      : undefined;
  const memberThreadSubjectId = memberThreadId ? patient?.id : undefined;

  React.useEffect(() => {
    onMemberThread?.(
      memberThreadId && memberThreadSubjectId
        ? { subjectId: memberThreadSubjectId, threadId: memberThreadId }
        : undefined,
    );
  }, [memberThreadId, memberThreadSubjectId, onMemberThread]);

  // Set scroll position after loading more messages
  React.useEffect(() => {
    const listRef = getListRef();

    if (listRef && heightRef.current !== null && !loadingMore) {
      listRef.scrollTop = listRef.scrollHeight - heightRef.current;
      heightRef.current = 0;
    }
  }, [loadingMore]);

  const [notify] = useNotify();

  const { uploadAttachments } = useChatAttachmentUpload({ userIds, projectId });

  const notifyRejectedFile = React.useCallback(
    (fileName: string, reason: AttachmentRejectionReason) => {
      const rejectionMessage = {
        'unsupported-type': messages.attachmentUnsupportedType,
        'too-large': messages.attachmentTooLarge,
        empty: messages.attachmentEmpty,
      }[reason];
      notify(
        formatMessage(rejectionMessage, {
          fileName,
          maxMb: Math.floor(MAX_CHAT_ATTACHMENT_BYTES / (1024 * 1024)),
        }),
        'error',
      );
    },
    [formatMessage, notify],
  );

  // Validation notices are side effects, so the accept/reject decision is made
  // here rather than inside the state updater, which React may replay.
  const stageFiles = React.useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }

      const accepted: StagedAttachment[] = [];
      let hitLimit = false;

      for (const file of files) {
        if (
          stagedAttachments.length + accepted.length >=
          MAX_CHAT_ATTACHMENTS
        ) {
          hitLimit = true;
          break;
        }

        const validation = validateAttachmentFile(file);
        if (!validation.ok) {
          notifyRejectedFile(file.name, validation.reason);
          continue;
        }

        accepted.push({ id: v4(), file, contentType: validation.contentType });
      }

      if (hitLimit) {
        notify(
          formatMessage(messages.attachmentLimitReached, {
            max: MAX_CHAT_ATTACHMENTS,
          }),
          'warning',
        );
      }

      if (accepted.length > 0) {
        setStagedAttachments(current => [...current, ...accepted]);
      }
    },
    [formatMessage, notify, notifyRejectedFile, stagedAttachments.length],
  );

  const removeStagedAttachment = React.useCallback((id: string) => {
    setStagedAttachments(current =>
      current.filter(attachment => attachment.id !== id),
    );
  }, []);

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    stageFiles(Array.from(event.target.files ?? []));
    // Reset so picking the same file twice in a row still fires a change event.
    event.target.value = '';
  };

  const handleComposerPaste = (event: React.ClipboardEvent) => {
    if (!attachmentsEnabled) {
      return;
    }
    const files = Array.from(event.clipboardData?.files ?? []);
    if (files.length > 0) {
      // Pasted screenshots arrive as files; let them attach instead of
      // dumping base64 into the editor.
      event.preventDefault();
      stageFiles(files);
    }
  };

  const handleComposerDrop = (event: React.DragEvent) => {
    if (!attachmentsEnabled) {
      return;
    }
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) {
      event.preventDefault();
      stageFiles(files);
    }
  };

  const onYReachStart = () => {
    if (loadingMore === true || (heightRef.current && heightRef?.current > 0)) {
      return;
    }
    setLoadingMore(true);

    // Remember message listRef scrollHeight
    const listRef = getListRef();
    heightRef.current = listRef ? listRef.scrollHeight : heightRef.current;

    setTimeout(async () => {
      try {
        await loadMore();
      } catch (error) {
        notify(formatMessage(messages.errorLoadingMore), 'error');
      } finally {
        setLoadingMore(false);
      }
    }, 1500);
  };

  const [isScrolledToBottom, setIsScrolledToBottom] = React.useState(true);

  const onChatScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const { offsetHeight, scrollTop, scrollHeight } = target;
    const scrollBottom = scrollHeight - scrollTop - offsetHeight;

    // < 50 because we don't show the scroll to bottom button if we're pretty close to the bottom
    setIsScrolledToBottom(scrollBottom < 50);
  };

  const scrollToBottom = () => {
    const listRef = getListRef();
    if (listRef) {
      listRef.scrollTop = listRef.scrollHeight;
    }
  };

  // Deep-link support: when a Zori Pulse citation requests a specific chat
  // message, scroll it into view and briefly highlight it. The list is not
  // virtualized (every loaded post is rendered), but the cited message may live
  // on an older, not-yet-loaded page, so we page backwards (bounded) until it
  // appears, then scroll the perfect-scrollbar container to it and flash it.
  // `loadMore`/`hasNextPage` are read through refs so the effect doesn't need to
  // re-run as pagination state changes.
  const [highlightedMessageId, setHighlightedMessageId] = React.useState<
    string | null
  >(null);
  const loadMoreRef = React.useRef(loadMore);
  loadMoreRef.current = loadMore;
  const hasNextPageRef = React.useRef(hasNextPage);
  hasNextPageRef.current = hasNextPage;

  React.useEffect(() => {
    if (!highlightMessageId) {
      return;
    }

    let cancelled = false;
    let pagesLoaded = 0;
    let domAttempts = 0;
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    const schedule = (fn: () => void, delay: number) => {
      timeouts.push(setTimeout(fn, delay));
    };

    const findAndHighlight = () => {
      if (cancelled) {
        return;
      }
      const element = document.getElementById(highlightMessageId);
      if (element) {
        // The message list scrolls via react-perfect-scrollbar (an
        // `overflow: hidden` container scrolled programmatically), so
        // `element.scrollIntoView()` is a no-op here. Center the message by
        // setting `scrollTop` on the same container the rest of the component
        // scrolls (see `scrollToBottom`). Fall back to `scrollIntoView` when
        // the container isn't found (e.g. jsdom in tests).
        const listRef = getListRef();
        if (listRef) {
          const containerRect = listRef.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const centerOffset =
            (listRef.clientHeight - element.offsetHeight) / 2;
          listRef.scrollTop +=
            elementRect.top - containerRect.top - centerOffset;
        } else {
          element.scrollIntoView({ block: 'center' });
        }
        setHighlightedMessageId(highlightMessageId);
        schedule(() => {
          if (!cancelled) {
            setHighlightedMessageId(current =>
              current === highlightMessageId ? null : current,
            );
          }
        }, CITATION_HIGHLIGHT_DURATION_MS);
        return;
      }

      // Not loaded yet: pull another older page if one exists.
      if (hasNextPageRef.current && pagesLoaded < CITATION_MAX_PAGES_TO_LOAD) {
        pagesLoaded += 1;
        loadMoreRef
          .current()
          .catch(() => undefined)
          .finally(() => {
            if (!cancelled) {
              schedule(findAndHighlight, CITATION_RETRY_INTERVAL_MS);
            }
          });
        return;
      }

      // Element may still be mounting after a render; retry a few times.
      domAttempts += 1;
      if (domAttempts < CITATION_MAX_DOM_ATTEMPTS) {
        schedule(findAndHighlight, CITATION_RETRY_INTERVAL_MS);
      }
    };

    // Defer the first lookup so the chat (which may have just switched into
    // member-history mode) has a chance to render its messages.
    schedule(findAndHighlight, 0);

    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
    };
    // `highlightNonce` lets repeat clicks on the same id re-run the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightMessageId, highlightNonce]);

  const classes = useStyles({
    loadingMore,
    scrollToBottomVisible: !isScrolledToBottom,
  });

  if (!isMemberChatHistory) {
    startPolling(3000);
  }
  const [createPrivatePostMutation] = useCreatePrivatePostMutation({});
  const [createPostReactionMutation] = useCreatePostReactionMutation({});
  const exportZoriReportMutation = apiHooks.useAPIMutation(
    'PUT /v1/fhir/dstu3/ClinicalImpression/:resourceId',
  );
  const sendMessageText = async (
    message: string,
    attachments: StagedAttachment[] = [],
  ) => {
    // Upload before anything is shown or persisted. A post that references a
    // file which failed to upload would be answered against nothing, so a
    // failure here has to abort the whole send.
    const uploadedAttachments = attachments.length
      ? await uploadAttachments(attachments)
      : [];

    // this will get overwritten by the real message when the server responds
    const eagerPost: PostDetailsFragment = {
      id: v4(),
      author: {
        __typename: 'User' as const,
        userId,
        profile: {
          displayName: 'You',
          __typename: 'UserProfile' as const,
        },
      },
      __typename: 'ActivePost' as const,
      createdAt: new Date().toISOString(),
      parentId: '',
      message,
      attachmentsV2: createEagerAttachmentsV2(uploadedAttachments, attachments),
    };
    addEagerPost(eagerPost);

    const metadata = {
      cohort_id: cohortId,
      project_id: projectId,
      ...(modelId ? { model_id: modelId } : {}),
    };

    await createPrivatePostMutation({
      variables: {
        input: {
          userIds,
          post: {
            id: eagerPost.id,
            message,
            metadata: JSON.stringify(metadata),
            ...(uploadedAttachments.length > 0
              ? { attachmentsV2: uploadedAttachments }
              : {}),
          },
          createConversation: false,
        },
      },
    });

    // Only counts once the post is persisted; a rejected create throws above.
    if (zoriSurface) {
      track('zori_chat_message_sent', { surface: zoriSurface });
    }

    if (!isMemberChatHistory) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore fetchMore has multiple signatures
      await fetchMore({
        variables: {
          userIds,
          filter: {
            order: PostOrder.NEWEST,
          },
        },
      });
    }
  };

  const submitComposer = async (innerText: string) => {
    // Backstop only. `sendOnReturnDisabled` is what actually blocks an
    // over-limit send: `MessageInput` clears the editor before invoking
    // `onSend`, so bailing out here would discard the text we are trying to
    // protect.
    if (innerText.length > MAX_MESSAGE_CHARS) {
      return;
    }

    const attachments = attachmentsEnabled ? stagedAttachments : [];

    // A message with attachments but no text is legitimate — "what do you make
    // of this?" is implied — but an empty send with neither is not.
    if (!innerText.trim() && attachments.length === 0) {
      return;
    }

    // The editor clears itself on send without firing `onChange`.
    setDraftLength(0);
    // Clear the tray up front so the chips cannot be edited mid-upload. They
    // are restored below if the send fails.
    setStagedAttachments([]);
    setIsUploadingAttachments(attachments.length > 0);

    // uses innerText instead of innerHTML which contains tags.
    // `MessageInput` drops the returned promise, so a failed send has to be
    // reported here or it surfaces as an unhandled rejection.
    try {
      await sendMessageText(innerText, attachments);
    } catch (error) {
      if (attachments.length > 0) {
        // Hand the files back rather than making the clinician re-pick them.
        setStagedAttachments(current => [...attachments, ...current]);
        notify(formatMessage(messages.attachmentUploadFailed), 'error');
      }
      reportErrorToBugsnag(
        new Error('Failed to send Zori chat message', { cause: error }),
      );
    } finally {
      setIsUploadingAttachments(false);
    }
  };

  const sendMessage: MessageInputProps['onSend'] = innerText =>
    submitComposer(innerText);

  /**
   * `MessageInput` swallows Enter while its editor is empty, so an
   * attachment-only message would silently do nothing. Send it ourselves.
   */
  const handleComposerKeyDown = (event: React.KeyboardEvent) => {
    if (
      event.key !== 'Enter' ||
      event.shiftKey ||
      draftLength > 0 ||
      stagedAttachments.length === 0 ||
      isUploadingAttachments
    ) {
      return;
    }

    event.preventDefault();
    void submitComposer('');
  };

  const sendFeedback = async (
    feedback: '👍' | '👎',
    {
      shouldAskZoriToFix = false,
      postId,
      commentOverride,
    }: {
      shouldAskZoriToFix?: boolean;
      /** Target post id; falls back to the currently hovered post. */
      postId?: string;
      /** Comment to submit instead of the modal textarea contents. */
      commentOverride?: string;
    } = {},
  ) => {
    const targetPostId = postId ?? hoveredPostId;
    if (!targetPostId) {
      return;
    }
    const trimmedComment = (commentOverride ?? comment).trim();
    if (feedback === '👎' && !trimmedComment) {
      return;
    }

    // Negative feedback carries the user's "ask Zori to fix" choice to the
    // agents Slack notification via a sentinel marker the handler strips.
    const reactionComment =
      feedback === '👎'
        ? `${trimmedComment} ${
            shouldAskZoriToFix ? FIX_REQUESTED_MARKER : FIX_NOT_REQUESTED_MARKER
          }`
        : trimmedComment;

    setIsSendingFeedback(true);
    try {
      await createPostReactionMutation({
        variables: {
          input: {
            postId: targetPostId,
            type: feedback,
            comment: reactionComment,
          },
        },
      });

      if (zoriSurface) {
        track('zori_chat_feedback_submitted', {
          sentiment: feedback === '👍' ? 'positive' : 'negative',
          fixRequested: shouldAskZoriToFix,
        });
      }

      if (feedback === '👎' && shouldAskZoriToFix) {
        await sendMessageText(trimmedComment);
      }

      setHoveredPostId(null);
      setComment('');
      setShowFeedbackModal(false);
    } finally {
      setIsSendingFeedback(false);
    }
  };

  // Bottom thumbs-up submits positive feedback immediately (no comment needed),
  // mirroring the modal's positive action with a lightweight confirmation.
  const handleThumbsUp = async (postId: string) => {
    try {
      await sendFeedback('👍', { postId, commentOverride: '' });
      notify(formatMessage(messages.feedbackSubmitted), 'info', 2000);
    } catch {
      notify(formatMessage(messages.feedbackError), 'error');
    }
  };

  // Bottom thumbs-down opens the existing feedback modal so the user can supply
  // the required comment (and, for clinician/population chat, choose whether to
  // ask Zori to fix the response).
  const handleThumbsDown = (postId: string) => {
    setHoveredPostId(postId);
    setComment('');
    setShowFeedbackModal(true);
  };

  const exportToZoriReport = React.useCallback(
    async (post: PostDetails_ActivePost_Fragment, message: string) => {
      if (!patient?.id) {
        return;
      }

      const impressionId = v4();
      try {
        const impression = await buildZoriChatReportClinicalImpression({
          impressionId,
          patientId: patient.id,
          projectId,
          postId: post.id,
          message,
          metadata: post.metadata,
          attachments: post.attachmentsV2.edges.map(edge => edge.node),
        });
        await exportZoriReportMutation.mutateAsync({
          ...impression,
          resourceId: impressionId,
        });
        if (zoriSurface) {
          track('zori_chat_report_export_completed', { outcome: 'success' });
        }
        notify(formatMessage(messages.exportToZoriReportSuccess), 'success');
      } catch {
        if (zoriSurface) {
          track('zori_chat_report_export_completed', { outcome: 'error' });
        }
        notify(formatMessage(messages.exportToZoriReportFailure), 'error');
      }
    },
    [
      patient?.id,
      projectId,
      exportZoriReportMutation,
      notify,
      formatMessage,
      track,
      zoriSurface,
    ],
  );

  const postsToRender = useMemo(() => {
    const reversePosts = posts ? [...posts].reverse() : [];
    const finishedPosts = reversePosts.filter(
      post =>
        !streamingMessages.find(message => message.responseId === post.id),
    );
    const streamingPosts = streamingMessages.map(message => {
      const tempPost: PostDetails_ActivePost_Fragment = {
        id: message.responseId,
        message: message.payload,
        author: {
          userId: feedbackAgentUsername,
          profile: { displayName: 'You', __typename: 'UserProfile' as const },
          __typename: 'User' as const,
        },
        createdAt: new Date(message.startedAt).toISOString(),
        parentId: '',
        __typename: 'ActivePost' as const,
        metadata: JSON.stringify(message.metadata),
        attachmentsV2: createEmptyAttachmentsV2(),
      };
      return tempPost;
    });

    return [...finishedPosts, ...streamingPosts];
  }, [posts, streamingMessages, feedbackAgentUsername]);

  const streamingPostIds = useMemo(
    () => new Set(streamingMessages.map(message => message.responseId)),
    [streamingMessages],
  );

  const rowsToRender = useMemo(
    () =>
      buildChatRowsForMessageList({
        posts: postsToRender,
      }),
    [postsToRender],
  );

  const currentUserIdForDirection = isMemberChatHistory
    ? patientUsername
    : userId;
  const shouldShowZoriNegativeFeedbackActions =
    isClinicianZoriChat || isPopulationAgentChat;

  const lastPost = postsToRender[postsToRender.length - 1];
  const isWaitingOnResponse =
    lastPost &&
    (isLastPostUserMessage(lastPost, userId) || isEphemeralPost(lastPost));

  if (loading && streamingMessages.length === 0) {
    return (
      <div className={clsx(classes.fullWidth, classes.fullHeight)}>
        <DotLoader />
      </div>
    );
  }

  return (
    <div
      className={clsx(
        classes.chatRoot,
        attachmentsEnabled && classes.composerWithAttachments,
      )}
    >
      <Modal
        isOpen={showFeedbackModal}
        onDismiss={() => setShowFeedbackModal(false)}
        title={formatMessage(messages.leaveFeedbackTitle)}
        size={1}
        fullWidth
        actions={
          <>
            {shouldShowZoriNegativeFeedbackActions ? (
              <>
                <Button
                  icon={ThumbsDown}
                  onClick={() =>
                    sendFeedback('👎', { shouldAskZoriToFix: true })
                  }
                  disabled={!comment.trim() || isSendingFeedback}
                >
                  {formatMessage(messages.reportAndAskZoriToFix)}
                </Button>
                <Button
                  variant="text"
                  onClick={() =>
                    sendFeedback('👎', { shouldAskZoriToFix: false })
                  }
                  disabled={!comment.trim() || isSendingFeedback}
                >
                  {formatMessage(messages.reportIssueOnly)}
                </Button>
              </>
            ) : (
              <Button
                icon={ThumbsDown}
                variant="outlined"
                onClick={() => sendFeedback('👎')}
                disabled={isSendingFeedback}
              >
                {formatMessage(messages.feedbackNegative)}
              </Button>
            )}
            <Button
              icon={ThumbsUp}
              variant="outlined"
              onClick={() => sendFeedback('👍')}
              disabled={isSendingFeedback}
            >
              {formatMessage(messages.feedbackPositive)}
            </Button>
          </>
        }
      >
        <textarea
          placeholder={formatMessage(messages.placeholderFeedback)}
          className={classes.feedbackTextArea}
          onChange={content => setComment(content.target.value)}
        ></textarea>
      </Modal>

      {/*
        Paste and drop are bound here rather than on the composer because
        `ChatContainer` only renders children it recognizes by component
        reference, so `MessageInput` cannot be wrapped. Both events bubble, and
        paste only fires when the composer has focus.
      */}
      <MainContainer
        className={classes.mainContainer}
        onPaste={attachmentsEnabled ? handleComposerPaste : undefined}
        onDrop={attachmentsEnabled ? handleComposerDrop : undefined}
        onDragOver={
          attachmentsEnabled ? event => event.preventDefault() : undefined
        }
        onKeyDown={attachmentsEnabled ? handleComposerKeyDown : undefined}
      >
        <AttachmentViewerModal isOpen={!!activeAttachmentImage}>
          {!!activeAttachmentImage && (
            <img
              alt="Attachment"
              src={activeAttachmentImage}
              className={classes.attachmentViewerImage}
            />
          )}
        </AttachmentViewerModal>
        <div
          className={clsx(
            classes.fullWidth,
            classes.scrollToBottomContainer,
            !isScrolledToBottom && classes.scrollToBottomContainerVisible,
          )}
        >
          <Button
            color="inverse"
            trailingIcon={ArrowDown2}
            onClick={scrollToBottom}
            className={classes.scrollToBottomButton}
          >
            {formatMessage(messages.scrollToBottom)}
          </Button>
        </div>

        <ChatContainer className={classes.chatContainer}>
          <MessageList
            onScroll={onChatScroll}
            typingIndicator={null} // we render our own
            loadingMore={false} // we render our own
            disableOnYReachWhenNoScroll={true}
            autoScrollToBottomOnMount={true}
            autoScrollToBottom={true}
            onYReachStart={() => {
              // prevents loading second page of messages when page loads
              if (!componentLoaded) {
                const listRef = getListRef();
                if (listRef) {
                  listRef.scrollTop = listRef.scrollHeight;
                }
                setComponentLoaded(true);
              }
              if (componentLoaded && hasNextPage) onYReachStart();
            }}
          >
            {loadingMore && (
              <div key={'load-more-indicator'} className={classes.fullWidth}>
                <DotLoader size={0} />
              </div>
            )}
            {(!postsToRender || postsToRender.length === 0) &&
              (isMemberChatHistory ? (
                <Box align="center" justify="center" fullHeight fullWidth>
                  <EmptyPageState
                    illustration={MessageCircle}
                    message={formatMessage(text.noMemberHistory)}
                  />
                </Box>
              ) : (
                <MessageList.Content>
                  <div className={classes.startingContent}>
                    <div>
                      <p>{formatMessage(text.helpContent1)}</p>
                      <p>{formatMessage(text.helpContent2)}</p>
                      <p>{formatMessage(text.helpContent3)}</p>
                      <p>{formatMessage(text.helpContent4)}</p>
                    </div>
                    <i className={classes.startingMessage}>
                      {formatMessage(text.startingMessage)}
                    </i>
                  </div>
                </MessageList.Content>
              ))}

            {rowsToRender.map(row => {
              if (row.kind === 'post') {
                return (
                  <ChatMessage
                    onMouseOver={() => setHoveredPostId(row.post.id)}
                    key={row.key}
                    post={row.post}
                    onClickFeedback={() => setShowFeedbackModal(true)}
                    onThumbsUp={() => handleThumbsUp(row.post.id)}
                    onThumbsDown={() => handleThumbsDown(row.post.id)}
                    onClickCitation={setCurrentReference}
                    onExportToZoriReport={
                      patient ? exportToZoriReport : undefined
                    }
                    isExportingToZoriReport={exportZoriReportMutation.isLoading}
                    isHovered={hoveredPostId === row.post.id}
                    isHighlighted={highlightedMessageId === row.post.id}
                    currentUserId={currentUserIdForDirection}
                    feedbackAgentUsername={feedbackAgentUsername}
                    isStreaming={streamingPostIds.has(row.post.id)}
                  />
                );
              }

              return (
                <ChatAttachmentMessage
                  key={row.key}
                  post={row.post}
                  attachment={row.attachment}
                  patient={patient}
                  currentUserId={currentUserIdForDirection}
                  onOpenImage={url => {
                    if (setActiveAttachmentImage) {
                      setActiveAttachmentImage(url);
                      return;
                    }

                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                  onOpenPdf={url =>
                    window.open(url, '_blank', 'noopener,noreferrer')
                  }
                  onOpenFile={url =>
                    window.open(url, '_blank', 'noopener,noreferrer')
                  }
                />
              );
            })}
            {isWaitingOnResponse && (
              <Message
                key={'typing-indicator'}
                model={{
                  message: '',
                  direction: 'incoming',
                  position: 'single',
                }}
              >
                <Avatar name={''} src={AgentIcon} />
                <Message.CustomContent>
                  <DotLoader size={0} />
                </Message.CustomContent>
              </Message>
            )}
          </MessageList>

          {(showZoriInputToolbox ||
            showCharacterCounter ||
            attachmentsEnabled) && (
            <InputToolbox>
              {(stagedAttachments.length > 0 ||
                isUploadingAttachments ||
                showCharacterCounter) && (
                <Box className={classes.attachmentTray}>
                  {stagedAttachments.length > 0 && (
                    <>
                      <Box className={classes.attachmentChips}>
                        {stagedAttachments.map(attachment => (
                          <Box
                            key={attachment.id}
                            className={classes.attachmentChip}
                            data-testid="zori-chat-attachment-chip"
                          >
                            <Text
                              size="caption"
                              className={classes.attachmentChipName}
                              title={attachment.file.name}
                            >
                              {attachment.file.name}
                            </Text>
                            <IconButton
                              icon={X}
                              size={0}
                              type="button"
                              aria-label={formatMessage(
                                messages.removeAttachment,
                                { fileName: attachment.file.name },
                              )}
                              disabled={isUploadingAttachments}
                              onClick={() =>
                                removeStagedAttachment(attachment.id)
                              }
                            />
                          </Box>
                        ))}
                      </Box>
                      <Text size="caption">
                        {formatMessage(messages.attachmentsNotSavedToChart)}
                      </Text>
                    </>
                  )}
                  <Box className={classes.attachmentTrayControls}>
                    {isUploadingAttachments && (
                      <Text
                        size="caption"
                        data-testid="zori-chat-attachment-uploading"
                      >
                        {formatMessage(messages.attachmentsUploading)}
                      </Text>
                    )}
                    {showCharacterCounter && (
                      <Text
                        size="caption"
                        color={isDraftOverLimit ? 'negative' : 'default'}
                        className={classes.zoriCharacterCounter}
                        data-testid="zori-chat-character-counter"
                        role={isDraftOverLimit ? 'alert' : undefined}
                      >
                        {formatMessage(
                          isDraftOverLimit
                            ? messages.messageTooLong
                            : messages.characterCount,
                          { count: draftLength, max: MAX_MESSAGE_CHARS },
                        )}
                      </Text>
                    )}
                  </Box>
                </Box>
              )}
              {(attachmentsEnabled || showZoriInputToolbox) && (
                <Box className={classes.composerActions}>
                  {attachmentsEnabled && (
                    <>
                      <IconButton
                        icon={Paperclip}
                        size={0}
                        type="button"
                        aria-label={formatMessage(messages.attachFile)}
                        data-testid="zori-chat-attach-button"
                        disabled={
                          isUploadingAttachments ||
                          stagedAttachments.length >= MAX_CHAT_ATTACHMENTS
                        }
                        onClick={() => fileInputRef.current?.click()}
                      />
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept={ATTACHMENT_FILE_PICKER_ACCEPT}
                        className={classes.visuallyHiddenInput}
                        data-testid="zori-chat-file-input"
                        onChange={handleFileInputChange}
                      />
                    </>
                  )}
                  {showZoriInputToolbox && (
                    <Popover
                      aria-label={formatMessage(messages.zoriChatSettings)}
                      placement="top-end"
                      usePortal={true}
                      className={classes.zoriSettingsPopover}
                      anchorElement={
                        <IconButton
                          icon={Settings}
                          aria-label={formatMessage(messages.zoriChatSettings)}
                          data-testid="zori-chat-settings-trigger"
                          size={0}
                          type="button"
                        />
                      }
                    >
                      <Box
                        direction="column"
                        className={classes.zoriSettingsFlyout}
                      >
                        <ModelSelect
                          modelId={modelId}
                          setModelId={setSelectedModelId}
                          models={models}
                          showDefaultOption={!isClinicianZoriChat}
                          hideLabel={false}
                          placement="top-start"
                          popoverStyle={{ zIndex: 1500 }}
                        />
                      </Box>
                    </Popover>
                  )}
                </Box>
              )}
            </InputToolbox>
          )}

          {!isMemberChatHistory && (
            <MessageInputWithPlaintext
              attachButton={false}
              placeholder={formatMessage(messages.placeholder)}
              onChange={(_innerHtml, textContent) =>
                setDraftLength(textContent.length)
              }
              onSend={sendMessage}
              sendButton={false}
              sendDisabled={isDraftOverLimit || isUploadingAttachments}
              sendOnReturnDisabled={isDraftOverLimit || isUploadingAttachments}
              plaintextOnly
            />
          )}
        </ChatContainer>

        {patient && (
          <Citation
            patient={patient}
            reference={currentReference}
            onCitationProcessed={() => setCurrentReference('')}
          />
        )}
      </MainContainer>
    </div>
  );
};

type ChatRow =
  | {
      kind: 'post';
      key: string;
      post: PostDetailsFragment;
    }
  | {
      kind: 'attachment';
      key: string;
      post: PostDetails_ActivePost_Fragment;
      attachment: ChatAttachmentNode;
    };

const buildChatRowsForMessageList = ({
  posts,
}: {
  posts: PostDetailsFragment[];
}): ChatRow[] => {
  return posts.flatMap(post => {
    const postRow: ChatRow = {
      kind: 'post',
      key: post.id,
      post,
    };

    if (post.__typename !== 'ActivePost') {
      return [postRow];
    }

    const attachmentRows: ChatRow[] = post.attachmentsV2.edges.map(edge => ({
      kind: 'attachment',
      key: `${post.id}#attachment#${edge.node.id}`,
      post,
      attachment: edge.node,
    }));

    // Attachments first, then caption/message.
    return [...attachmentRows, postRow];
  });
};

function isEphemeralPost(post: PostDetailsFragment): boolean {
  return (
    post.__typename === 'ActivePost' &&
    JSON.parse(post.metadata || '{}').type === 'ephemeral'
  );
}

function isLastPostUserMessage(
  post: PostDetailsFragment,
  userId: string,
): boolean {
  return post.__typename === 'ActivePost' && post.author?.userId === userId;
}
